// Localização: ./src/api/api.js (Versão Corrigida para Lógica de Ambiente)

import axios from 'axios';

const PROD_URL = process.env.REACT_APP_API_BASE_URL_PROD;
// Não precisamos declarar NODE_ENV, podemos usá-lo diretamente
// const NODE_ENV = process.env.NODE_ENV; 

const getBaseURL = () => {
    
    // 🛑 MUDANÇA CRÍTICA: Priorizar o ambiente de DESENVOLVIMENTO
    // Se o React está rodando via `npm start`, NODE_ENV é 'development'.
    if (process.env.NODE_ENV === 'development') { 
        // 1. Em desenvolvimento, sempre use o local host
        return 'http://localhost:3001/api';
    } 
    
    // 2. Em Produção (ou ambiente desconhecido), use a variável de PROD.
    if (PROD_URL) {
        // Garantindo que a URL de PROD contenha /api
        return PROD_URL.endsWith('/api') ? PROD_URL : `${PROD_URL}/api`;
    }

    // Fallback de segurança (se nenhuma variável existir)
    return 'http://localhost:3001';
};

const api = axios.create({
    // Usa a função para definir a baseURL
    baseURL: getBaseURL(), 
    headers: {
         'Content-Type': 'application/json',
    }
});

// Inicializa o token do localStorage na inicialização do módulo
let currentToken = localStorage.getItem('token');

export const setAuthToken = (token) => {
    currentToken = token;
    if (token) {
        localStorage.setItem('token', token);
        // Atualiza o header do Axios imediatamente
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
    }
};

// Sincroniza o token do localStorage na inicialização
if (currentToken) {
    api.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
}

api.interceptors.request.use(
    (config) => {
        // Sempre verifica o localStorage para garantir sincronização após refresh
        const tokenFromStorage = localStorage.getItem('token');
        if (tokenFromStorage && tokenFromStorage !== currentToken) {
            currentToken = tokenFromStorage;
            api.defaults.headers.common['Authorization'] = `Bearer ${tokenFromStorage}`;
        }
        
        if (currentToken) {
            config.headers.Authorization = `Bearer ${currentToken}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Interceptor de resposta para lidar com erros 401 (token inválido/expirado) e 403 (sem assinatura)
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Token inválido ou expirado
            currentToken = null;
            localStorage.removeItem('token');
            delete api.defaults.headers.common['Authorization'];
            // Opcional: redirecionar para login via evento customizado
            window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason: 'unauthorized' } }));
        } else if (error.response?.status === 403) {
            // ✅ CORREÇÃO: Verificar se é admin antes de redirecionar
            // Decodificar token para verificar role
            let isAdmin = false;
            try {
                const token = localStorage.getItem('token');
                if (token) {
                    // Decodificar JWT sem verificar assinatura (apenas para ler payload)
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    isAdmin = ['global_admin', 'super_admin', 'admin'].includes(payload.role);
                }
            } catch (e) {
                // Se não conseguir decodificar, não é admin
            }
            
            // Verificar se é erro relacionado a assinatura
            const message = error.response?.data?.message || '';
            if ((message.includes('não possui uma assinatura ativa') || 
                message.includes('não está vinculado a uma organização')) && !isAdmin) {
                // Redirecionar para página sem assinatura apenas se não estiver já nela
                // e não estiver em páginas de assinatura (sucesso, pendente, etc)
                const currentPath = window.location.pathname;
                const isInAssinaturaPages = currentPath.includes('/config/assinatura/');
                const isInPricing = currentPath.includes('/pricing');
                const isInLogin = currentPath.includes('/login');
                
                // Não redirecionar se já estiver em páginas relacionadas a assinatura, pricing ou login
                if (!isInAssinaturaPages && !isInPricing && !isInLogin) {
                    // Usar setTimeout para evitar redirecionamento durante renderização
                    setTimeout(() => {
                        if (window.location.pathname === currentPath) {
                            window.location.href = '/config/assinatura/sem-assinatura';
                        }
                    }, 100);
                }
            }
        }
        return Promise.reject(error);
    }
);


// --------------------------------------------------------------------------------
// 2. FUNÇÕES DE AUTENTICAÇÃO (ROTAS CORRIGIDAS)
// --------------------------------------------------------------------------------

export const login = async ({ email, password }) => {
    // ROTA CORRETA: /auth/login (baseURL já é .../api)
    return api.post('/auth/login', { email, password });
};

/**
 * Obtém apenas as permissões do usuário autenticado (endpoint otimizado)
 * Mais leve e rápido que /usuarios/me, ideal para carregamento inicial
 */
export const getPermissions = async () => {
    return api.get('/auth/permissions');
};

export const registerOrg = async (data) => {
    // 🛑 CORRIGIDO: Removido o '/api' duplicado.
    return api.post('/organizacao/register', data); 
};

/**
 * Cria uma organização para o usuário autenticado (sem organização)
 * O usuário se torna proprietário automaticamente
 */
export const createOrganization = async (data) => {
    return api.post('/organizacao/create', data);
};

// --------------------------------------------------------------------------------
// 3. FUNÇÕES DE USUÁRIOS (GESTÃO DE PESSOAL) - Recurso: usuarios 
// --------------------------------------------------------------------------------

export const getUsers = async () => {
    return api.get('/usuarios'); 
};

export const createUser = async (userData) => {
    return api.post('/usuarios', userData);
};

export const updateUser = async (userId, userData) => {
    return api.put(`/usuarios/${userId}`, userData);
};

export const deleteUser = async (userId) => {
    return api.delete(`/usuarios/${userId}`);
};

/**
 * Obtém os dados do perfil do próprio usuário autenticado
 */
export const getMyProfile = async () => {
    return api.get('/usuarios/me');
};

/**
 * Atualiza os dados do perfil do próprio usuário autenticado
 * @param {FormData|object} profileData - FormData (com foto) ou objeto { nome_completo?, email?, telefone?, senha?, senha_atual? }
 */
export const updateMyProfile = async (profileData) => {
    // Se for FormData (upload de arquivo), não definir Content-Type (deixa o navegador definir com boundary)
    if (profileData instanceof FormData) {
        return api.put('/usuarios/me', profileData, {
            headers: {
                'Content-Type': undefined, // Permite que o navegador defina o Content-Type com boundary
            },
        });
    }
    // Se for objeto JSON normal, usar o comportamento padrão
    return api.put('/usuarios/me', profileData);
};

/**
 * Faz upload da assinatura digital do usuário (PNG)
 * @param {FormData} formData - FormData com o campo 'assinatura' contendo o arquivo PNG
 */
export const uploadAssinatura = async (formData) => {
    return api.put('/usuarios/me/assinatura', formData, {
        headers: {
            'Content-Type': undefined, // Permite que o navegador defina o Content-Type com boundary
        },
    });
};

// --------------------------------------------------------------------------------
// 3.1. FUNÇÕES DE VINCULAÇÃO DE USUÁRIOS - Recurso: vinculacao
// --------------------------------------------------------------------------------

/**
 * Lista usuários pendentes de vinculação (sem organização)
 */
export const getUsuariosPendentes = async () => {
    return api.get('/vinculacao/usuarios-pendentes');
};

/**
 * Busca usuário por email exato
 */
export const buscarUsuarioPorEmail = async (email) => {
    return api.get(`/vinculacao/buscar-por-email/${encodeURIComponent(email)}`);
};

/**
 * Busca usuários por domínio de email
 */
export const buscarUsuariosPorDominio = async (dominio) => {
    // Remove @ se presente
    const dominioLimpo = dominio.replace('@', '');
    return api.get(`/vinculacao/buscar-por-dominio/${encodeURIComponent(dominioLimpo)}`);
};

/**
 * Vincula usuário à organização
 * @param {object} data - { usuario_id, role }
 */
export const vincularUsuario = async (data) => {
    return api.post('/vinculacao/vincular', data);
};

/**
 * Desvincula usuário da organização
 */
export const desvincularUsuario = async (usuarioId) => {
    return api.post(`/vinculacao/desvincular/${usuarioId}`);
};

/**
 * Lista vínculos pendentes de aceitação do usuário logado
 */
export const getVinculosPendentes = async () => {
    return api.get('/vinculacao/vinculos-pendentes');
};

/**
 * Aceita um vínculo pendente
 * @param {object} data - { organizacao_id } (opcional)
 */
export const aceitarVinculo = async (data = {}) => {
    return api.post('/vinculacao/aceitar-vinculo', data);
};

/**
 * Rejeita um vínculo pendente
 * @param {object} data - { organizacao_id } (opcional)
 */
export const rejeitarVinculo = async (data = {}) => {
    return api.post('/vinculacao/rejeitar-vinculo', data);
};


// --------------------------------------------------------------------------------
// 4. FUNÇÕES DE PACIENTES - Recurso: pacientes
// --------------------------------------------------------------------------------

export const getPacientes = async (query = {}) => {
    return api.get('/pacientes', { params: query }); 
};

/**
 * Busca pacientes e usuários para autocomplete no chat
 * @param {string} searchTerm - Termo de busca (mínimo 2 caracteres)
 * @returns {Promise} Promise com { pacientes: [], usuarios: [] }
 */
export const autocompletePacientesUsuarios = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
        return { data: { pacientes: [], usuarios: [] } };
    }
    return api.get('/pacientes/autocomplete', { params: { q: searchTerm.trim() } });
};

export const createPaciente = async (pacienteData) => {
    return api.post('/pacientes', pacienteData);
};

export const updatePaciente = async (pacienteId, pacienteData) => {
    return api.put(`/pacientes/${pacienteId}`, pacienteData);
};

export const deletePaciente = async (pacienteId) => {
    return api.delete(`/pacientes/${pacienteId}`);
};


// --------------------------------------------------------------------------------
// 5. FUNÇÕES DE AGENDAMENTOS - Recurso: agendamentos
// --------------------------------------------------------------------------------

export const getAgendamentos = async (query = {}) => {
    return api.get('/agendamentos', { params: query }); 
};

export const createAgendamento = async (agendamentoData) => {
    return api.post('/agendamentos', agendamentoData);
};

export const getAgendamentosPendentes = async (query = {}) => {
    return api.get('/agendamentos/pendentes', { params: query }); 
};


// --------------------------------------------------------------------------------
// 6. FUNÇÕES DE PONTO (CHECK IN OUT) - Recurso: ponto
// --------------------------------------------------------------------------------

export const getPontoStatus = async () => {
    return api.get('/ponto/status'); 
};

export const registerCheckIn = async (payload) => {
    return api.post('/ponto/check-in', payload); 
};

export const registerCheckOut = async (payload) => {
    return api.post('/ponto/check-out', payload); 
};

export const getPontoRecords = async (query = {}) => {
    return api.get('/ponto/registros', { params: query }); 
};


// --------------------------------------------------------------------------------
// 7. FUNÇÕES DE DASHBOARD - Recurso: dashboard
// --------------------------------------------------------------------------------

/**
 * Obtém o resumo principal (KPIs) do dashboard.
 */
export const getDashboardSummary = async () => {
    return api.get('/dashboard/summary');
};

/**
 * Obtém a distribuição de tarefas concluídas (Gráfico de Pizza).
 */
export const getTaskDistributionApi = async () => {
    return api.get('/dashboard/task-distribution');
};

/**
 * Obtém a carga de trabalho futura por profissional (Gráfico de Barras).
 */
export const getWorkloadByProfessionalApi = async () => {
    return api.get('/dashboard/workload-by-professional');
};

/**
 * Verifica os atrasadinho
 */
export const getPontoDesviosRecentes = async () => {
    return api.get('/dashboard/ponto-desvios-recentes');
};

/**
 * Verifica os estoque alertas
 */
export const getEstoqueAlertas = async () => {
    return api.get('/dashboard/estoque-alertas');
};


// --------------------------------------------------------------------------------
// 8. Exportações para prontuário do paciente
// --------------------------------------------------------------------------------

/**
 * Busca o resumo do prontuário (cabeçalho, KPIs, última SV) de um paciente.
 * @param {number} pacienteId - O ID do paciente para buscar o resumo.
 */
export const getProntuarioSummary = async (pacienteId) => {
    return api.get(`/pacientes/${pacienteId}/prontuario/summary`);
};

/**
 * Busca full-text no prontuário do paciente
 * @param {number} pacienteId - ID do paciente
 * @param {string} query - Termo de busca
 * @param {object} filters - Filtros opcionais { type, startDate, endDate, usuarioId }
 * @param {AbortSignal} signal - Signal para cancelamento da requisição
 */
export const searchProntuario = async (pacienteId, query, filters = {}, signal = null) => {
    const params = {
        q: query,
        ...filters
    };
    const config = signal ? { params, signal } : { params };
    return api.get(`/pacientes/${pacienteId}/prontuario/search`, config);
};

/**
 * Filtra registros do prontuário por critérios específicos
 * @param {number} pacienteId - ID do paciente
 * @param {object} filters - Filtros { type, startDate, endDate, usuarioId, status }
 */
export const filterProntuario = async (pacienteId, filters) => {
    return api.get(`/pacientes/${pacienteId}/prontuario/filter`, { params: filters });
};

// ============================================================================
// FUNÇÕES DE PRONTUÁRIO DO PROFISSIONAL
// ============================================================================

/**
 * Busca o resumo do prontuário (cabeçalho, KPIs) de um profissional.
 * @param {number} profissionalId - O ID do profissional para buscar o resumo.
 */
export const getProntuarioProfissionalSummary = async (profissionalId) => {
    return api.get(`/profissionais/${profissionalId}/prontuario/summary`);
};

/**
 * Busca o prontuário completo de um profissional.
 * @param {number} profissionalId - O ID do profissional.
 */
export const getProntuarioProfissional = async (profissionalId) => {
    return api.get(`/profissionais/${profissionalId}/prontuario`);
};

/**
 * Cria um novo prontuário para um profissional.
 * @param {number} profissionalId - O ID do profissional.
 * @param {object} dados - Dados do prontuário.
 */
export const createProntuarioProfissional = async (profissionalId, dados) => {
    return api.post(`/profissionais/${profissionalId}/prontuario`, dados);
};

/**
 * Atualiza o prontuário de um profissional.
 * @param {number} profissionalId - O ID do profissional.
 * @param {object} dados - Dados a serem atualizados.
 */
export const updateProntuarioProfissional = async (profissionalId, dados) => {
    return api.put(`/profissionais/${profissionalId}/prontuario`, dados);
};

// ============================================================================
// FUNÇÕES DE RH DO PROFISSIONAL
// ============================================================================

/**
 * Busca resumo consolidado de RH do profissional
 */
export const getResumoRH = async (profissionalId) => {
    return api.get(`/profissionais/${profissionalId}/rh/resumo`);
};

/**
 * Busca histórico de salários
 */
export const getSalarios = async (profissionalId) => {
    return api.get(`/profissionais/${profissionalId}/rh/salarios`);
};

/**
 * Registra novo salário
 */
export const createSalario = async (profissionalId, dados) => {
    return api.post(`/profissionais/${profissionalId}/rh/salarios`, dados);
};

/**
 * Busca histórico de faltas
 */
export const getFaltas = async (profissionalId, filters = {}) => {
    return api.get(`/profissionais/${profissionalId}/rh/faltas`, { params: filters });
};

/**
 * Registra nova falta
 */
export const createFalta = async (profissionalId, dados) => {
    return api.post(`/profissionais/${profissionalId}/rh/faltas`, dados);
};

/**
 * Busca histórico de férias
 */
export const getFerias = async (profissionalId, filters = {}) => {
    return api.get(`/profissionais/${profissionalId}/rh/ferias`, { params: filters });
};

/**
 * Registra novo período de férias
 */
export const createFerias = async (profissionalId, dados) => {
    return api.post(`/profissionais/${profissionalId}/rh/ferias`, dados);
};

/**
 * Busca histórico de banco de horas
 */
export const getBancoHoras = async (profissionalId, filters = {}) => {
    return api.get(`/profissionais/${profissionalId}/rh/banco-horas`, { params: filters });
};

/**
 * Busca histórico de avaliações de desempenho
 */
export const getAvaliacoes = async (profissionalId, filters = {}) => {
    return api.get(`/profissionais/${profissionalId}/rh/avaliacoes`, { params: filters });
};

/**
 * Busca timeline de eventos do prontuário
 * @param {number} pacienteId - ID do paciente
 * @param {object} options - Opções { startDate, endDate, type, usuarioId, page, limit }
 */
export const getTimeline = async (pacienteId, options = {}) => {
    return api.get(`/pacientes/${pacienteId}/timeline`, { params: options });
};

/**
 * Busca estatísticas da timeline
 * @param {number} pacienteId - ID do paciente
 * @param {object} options - Opções { startDate, endDate }
 */
export const getTimelineStats = async (pacienteId, options = {}) => {
    return api.get(`/pacientes/${pacienteId}/timeline/stats`, { params: options });
};

/**
 * Busca o histórico de Sinais Vitais (SVs) de um paciente para o gráfico.
 * @param {number} pacienteId - O ID do paciente.
 */
export const getHistoricoSinaisVitais = async (pacienteId) => {
    return api.get(`/pacientes/${pacienteId}/sinais-vitais/historico`); 
};

/**
 * Busca o histórico de cuidados do paciente com paginação e filtros.
 * @param {number} pacienteId
 * @param {object} params - { page, limit, sortBy, order, startDate, endDate, tipo }
 */
export const getHistoricoCuidados = async (pacienteId, { limit = 50 } = {}) => {
    // Tenta rota do prontuário; se 404, faz fallback para rota antiga do diário
    try {
        return await api.get(`/pacientes/${pacienteId}/diario/historico`, { params: { limit } });
    } catch (err) {
        if (err?.response?.status === 404) {
            return await api.get(`/diario/paciente/${pacienteId}`);
        }
        throw err;
    }
};

/**
 * Busca o histórico completo de medicações do paciente.
 * @param {number} pacienteId
 * @param {object} params - { limit, startDate, endDate }
 */
export const getHistoricoMedicacoes = async (pacienteId, { limit = 100, startDate = null, endDate = null } = {}) => {
    const params = {};
    if (limit) params.limit = limit;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    return await api.get(`/pacientes/${pacienteId}/medicacoes/historico`, { params });
};

// --------------------------------------------------------------------------------
// 9. FUNÇÕES DE EQUIPAMENTOS E VÍNCULOS - Recurso: equipamentos
// --------------------------------------------------------------------------------

/**
 * Cria um vínculo entre um equipamento e um paciente.
 */
export const vincularEquipamentoApi = async (data) => {
    return api.post('/equipamentos/vincular', data); 
};

/**
 * Encerra um vínculo ativo de equipamento, desvinculando-o do paciente.
 */
export const desvincularEquipamentoApi = async (vinculoId) => {
    return api.delete(`/equipamentos/desvincular/${vinculoId}`); 
};

/**
 * Busca os equipamentos vinculados a um paciente específico.
 * @param {number} pacienteId - O ID do paciente.
 */
export const getEquipamentosByPaciente = async (pacienteId) => {
    return api.get(`/equipamentos/paciente/${pacienteId}`);
};

/**
 * Busca o histórico completo de equipamentos de um paciente (ativos e desvinculados).
 * @param {number} pacienteId - O ID do paciente.
 */
export const getHistoricoEquipamentosByPaciente = async (pacienteId) => {
    return api.get(`/equipamentos/paciente/${pacienteId}/historico`);
};


// --------------------------------------------------------------------------------
// 10. FUNÇÕES DE ESTOQUE/LOTES - Recurso: estoque
// --------------------------------------------------------------------------------

/**
 * Busca lotes ativos (não vencidos, saldo > 0) para um medicamento específico, 
 * que sejam OU alocados ao paciente OU de uso geral.
 */
export const getLotesDisponiveisParaPlanoApi = async (medicamentoId, pacienteId) => {
    return api.get(`/lotes/disponiveis/${medicamentoId}/${pacienteId}`);
};

// --------------------------------------------------------------------------------
// 11. FUNÇÕES DE RBAC - Recurso: rbac/config
// --------------------------------------------------------------------------------

/**
 * @function listRbacConfig
 * @description Lista todas as configurações de permissão de RBAC para a organização.
 * Rota: GET /api/rbac/config
 */
export const listRbacConfig = async () => {
    // Rota correta: /rbac/config (baseURL já é .../api)
    return api.get('/rbac/config');
};

/**
 * @function updateRbacPermission
 * @description Atualiza ou insere (UPSERT) uma única configuração de permissão.
 * Rota: POST /api/rbac/config
 * @param {object} permissionData - { role, module_key, can_view, actions }
 */
export const updateRbacPermission = async (permissionData) => {
    // Rota correta: /rbac/config
    return api.post('/rbac/config', permissionData);
};


// --------------------------------------------------------------------------------
// 12. FUNÇÕES DE HISTÓRICO MÉDICO COMPLETO - Recurso: prontuario
// --------------------------------------------------------------------------------

/**
 * Busca histórico médico do paciente (doenças, cirurgias, hospitalizações)
 * @param {number} pacienteId - ID do paciente
 * @param {object} options - Opções { tipo, startDate, endDate }
 */
export const getHistoricoMedico = async (pacienteId, options = {}) => {
    return api.get(`/pacientes/${pacienteId}/historico-medico`, { params: options });
};

/**
 * Cria um novo registro no histórico médico
 * @param {number} pacienteId - ID do paciente
 * @param {object} data - Dados do histórico médico
 */
export const createHistoricoMedico = async (pacienteId, data) => {
    return api.post(`/pacientes/${pacienteId}/historico-medico`, data);
};

/**
 * Atualiza um registro do histórico médico
 * @param {number} pacienteId - ID do paciente
 * @param {number} historicoId - ID do registro
 * @param {object} data - Dados a atualizar
 */
export const updateHistoricoMedico = async (pacienteId, historicoId, data) => {
    return api.put(`/pacientes/${pacienteId}/historico-medico/${historicoId}`, data);
};

/**
 * Remove um registro do histórico médico
 * @param {number} pacienteId - ID do paciente
 * @param {number} historicoId - ID do registro
 */
export const deleteHistoricoMedico = async (pacienteId, historicoId) => {
    return api.delete(`/pacientes/${pacienteId}/historico-medico/${historicoId}`);
};

/**
 * Busca alergias do paciente
 * @param {number} pacienteId - ID do paciente
 */
export const getAlergias = async (pacienteId) => {
    return api.get(`/pacientes/${pacienteId}/alergias`);
};

/**
 * Cria um novo registro de alergia
 * @param {number} pacienteId - ID do paciente
 * @param {object} data - Dados da alergia
 */
export const createAlergia = async (pacienteId, data) => {
    return api.post(`/pacientes/${pacienteId}/alergias`, data);
};

/**
 * Atualiza um registro de alergia
 * @param {number} pacienteId - ID do paciente
 * @param {number} alergiaId - ID da alergia
 * @param {object} data - Dados a atualizar
 */
export const updateAlergia = async (pacienteId, alergiaId, data) => {
    return api.put(`/pacientes/${pacienteId}/alergias/${alergiaId}`, data);
};

/**
 * Remove um registro de alergia
 * @param {number} pacienteId - ID do paciente
 * @param {number} alergiaId - ID da alergia
 */
export const deleteAlergia = async (pacienteId, alergiaId) => {
    return api.delete(`/pacientes/${pacienteId}/alergias/${alergiaId}`);
};

/**
 * Busca histórico de vacinas do paciente
 * @param {number} pacienteId - ID do paciente
 */
export const getVacinas = async (pacienteId) => {
    return api.get(`/pacientes/${pacienteId}/vacinas`);
};

/**
 * Cria um novo registro de vacina
 * @param {number} pacienteId - ID do paciente
 * @param {object} data - Dados da vacina
 */
export const createVacina = async (pacienteId, data) => {
    return api.post(`/pacientes/${pacienteId}/vacinas`, data);
};

/**
 * Atualiza um registro de vacina
 * @param {number} pacienteId - ID do paciente
 * @param {number} vacinaId - ID da vacina
 * @param {object} data - Dados a atualizar
 */
export const updateVacina = async (pacienteId, vacinaId, data) => {
    return api.put(`/pacientes/${pacienteId}/vacinas/${vacinaId}`, data);
};

/**
 * Remove um registro de vacina
 * @param {number} pacienteId - ID do paciente
 * @param {number} vacinaId - ID da vacina
 */
export const deleteVacina = async (pacienteId, vacinaId) => {
    return api.delete(`/pacientes/${pacienteId}/vacinas/${vacinaId}`);
};

/**
 * Busca histórico de exames do paciente
 * @param {number} pacienteId - ID do paciente
 * @param {object} options - Opções { tipo_exame, status }
 */
export const getExames = async (pacienteId, options = {}) => {
    return api.get(`/pacientes/${pacienteId}/exames`, { params: options });
};

/**
 * Cria um novo registro de exame
 * @param {number} pacienteId - ID do paciente
 * @param {object} data - Dados do exame
 */
export const createExame = async (pacienteId, data) => {
    return api.post(`/pacientes/${pacienteId}/exames`, data);
};

/**
 * Atualiza um registro de exame
 * @param {number} pacienteId - ID do paciente
 * @param {number} exameId - ID do exame
 * @param {object} data - Dados a atualizar
 */
export const updateExame = async (pacienteId, exameId, data) => {
    return api.put(`/pacientes/${pacienteId}/exames/${exameId}`, data);
};

/**
 * Remove um registro de exame
 * @param {number} pacienteId - ID do paciente
 * @param {number} exameId - ID do exame
 */
export const deleteExame = async (pacienteId, exameId) => {
    return api.delete(`/pacientes/${pacienteId}/exames/${exameId}`);
};

/**
 * Busca histórico familiar do paciente
 * @param {number} pacienteId - ID do paciente
 */
export const getHistoricoFamiliar = async (pacienteId) => {
    return api.get(`/pacientes/${pacienteId}/historico-familiar`);
};

/**
 * Cria um novo registro de histórico familiar
 * @param {number} pacienteId - ID do paciente
 * @param {object} data - Dados do histórico familiar
 */
export const createHistoricoFamiliar = async (pacienteId, data) => {
    return api.post(`/pacientes/${pacienteId}/historico-familiar`, data);
};

/**
 * Atualiza um registro de histórico familiar
 * @param {number} pacienteId - ID do paciente
 * @param {number} historicoFamiliarId - ID do registro
 * @param {object} data - Dados a atualizar
 */
export const updateHistoricoFamiliar = async (pacienteId, historicoFamiliarId, data) => {
    return api.put(`/pacientes/${pacienteId}/historico-familiar/${historicoFamiliarId}`, data);
};

/**
 * Remove um registro de histórico familiar
 * @param {number} pacienteId - ID do paciente
 * @param {number} historicoFamiliarId - ID do registro
 */
export const deleteHistoricoFamiliar = async (pacienteId, historicoFamiliarId) => {
    return api.delete(`/pacientes/${pacienteId}/historico-familiar/${historicoFamiliarId}`);
};

// --------------------------------------------------------------------------------
// 13. FUNÇÕES DE NOTAS CLÍNICAS ESTRUTURADAS - Recurso: prontuario
// --------------------------------------------------------------------------------

/**
 * Busca notas clínicas do paciente
 * @param {number} pacienteId - ID do paciente
 * @param {object} options - Opções { tipo, categoria, rascunho, startDate, endDate, usuarioId, search, tags, limit, offset }
 */
export const getNotasClinicas = async (pacienteId, options = {}) => {
    // Remove valores undefined/null e converte boolean para string explícita
    // para evitar problemas na serialização do axios
    const cleanParams = Object.entries(options).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null) {
            // Converte boolean para string 'true'/'false' para garantir serialização correta
            if (typeof value === 'boolean') {
                acc[key] = value ? 'true' : 'false';
            } else {
                acc[key] = value;
            }
        }
        return acc;
    }, {});
    return api.get(`/pacientes/${pacienteId}/notas-clinicas`, { params: cleanParams });
};

/**
 * Busca uma nota clínica específica
 * @param {number} pacienteId - ID do paciente
 * @param {number} notaId - ID da nota
 */
export const getNotaClinicaById = async (pacienteId, notaId) => {
    return api.get(`/pacientes/${pacienteId}/notas-clinicas/${notaId}`);
};

/**
 * Cria uma nova nota clínica
 * @param {number} pacienteId - ID do paciente
 * @param {object} data - Dados da nota
 */
export const createNotaClinica = async (pacienteId, data) => {
    return api.post(`/pacientes/${pacienteId}/notas-clinicas`, data);
};

/**
 * Atualiza uma nota clínica
 * @param {number} pacienteId - ID do paciente
 * @param {number} notaId - ID da nota
 * @param {object} data - Dados a atualizar
 */
export const updateNotaClinica = async (pacienteId, notaId, data) => {
    return api.put(`/pacientes/${pacienteId}/notas-clinicas/${notaId}`, data);
};

/**
 * Remove uma nota clínica
 * @param {number} pacienteId - ID do paciente
 * @param {number} notaId - ID da nota
 */
export const deleteNotaClinica = async (pacienteId, notaId) => {
    return api.delete(`/pacientes/${pacienteId}/notas-clinicas/${notaId}`);
};

/**
 * Assina uma nota clínica digitalmente
 * @param {number} pacienteId - ID do paciente
 * @param {number} notaId - ID da nota
 */
export const assinarNotaClinica = async (pacienteId, notaId) => {
    return api.post(`/pacientes/${pacienteId}/notas-clinicas/${notaId}/assinar`);
};

/**
 * Cria uma nova versão de uma nota assinada
 * @param {number} pacienteId - ID do paciente
 * @param {number} notaId - ID da nota original
 * @param {object} data - Dados da nova versão
 */
export const criarNovaVersaoNota = async (pacienteId, notaId, data) => {
    return api.post(`/pacientes/${pacienteId}/notas-clinicas/${notaId}/nova-versao`, data);
};

/**
 * Busca templates de notas disponíveis
 * @param {number} pacienteId - ID do paciente
 * @param {object} options - Opções { tipo, ativo }
 */
export const getTemplatesNotas = async (pacienteId, options = {}) => {
    return api.get(`/pacientes/${pacienteId}/notas-clinicas/templates`, { params: options });
};

/**
 * Busca um template específico
 * @param {number} pacienteId - ID do paciente
 * @param {number} templateId - ID do template
 */
export const getTemplateById = async (pacienteId, templateId) => {
    return api.get(`/pacientes/${pacienteId}/notas-clinicas/templates/${templateId}`);
};

/**
 * Busca notas por termo (full-text search)
 * @param {number} pacienteId - ID do paciente
 * @param {string} termo - Termo de busca
 * @param {object} options - Opções { limit }
 */
export const buscarNotas = async (pacienteId, termo, options = {}) => {
    return api.get(`/pacientes/${pacienteId}/notas-clinicas/buscar`, { 
        params: { termo, ...options } 
    });
};

/**
 * Busca estatísticas das notas do paciente
 * @param {number} pacienteId - ID do paciente
 */
export const getEstatisticasNotas = async (pacienteId) => {
    return api.get(`/pacientes/${pacienteId}/notas-clinicas/estatisticas`);
};

// ============================================================================
// FUNÇÕES DE API: Documentos do Paciente
// ============================================================================

/**
 * Faz upload de um documento PDF para o paciente
 * @param {number} pacienteId - ID do paciente
 * @param {FormData} formData - FormData contendo o arquivo e metadados (documento, titulo, descricao, categoria)
 */
export const uploadDocumentoPaciente = async (pacienteId, formData) => {
    return api.post(`/pacientes/${pacienteId}/documentos`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
};

/**
 * Lista documentos do paciente com filtros opcionais
 * @param {number} pacienteId - ID do paciente
 * @param {object} filtros - Filtros { categoria, entidade_origem, search, page, limit }
 */
export const listarDocumentosPaciente = async (pacienteId, filtros = {}) => {
    return api.get(`/pacientes/${pacienteId}/documentos`, { params: filtros });
};

/**
 * Obtém metadados de um documento específico
 * @param {number} pacienteId - ID do paciente
 * @param {number} documentoId - ID do documento
 */
export const obterDocumentoPaciente = async (pacienteId, documentoId) => {
    return api.get(`/pacientes/${pacienteId}/documentos/${documentoId}`);
};

/**
 * Faz download do arquivo PDF
 * @param {number} pacienteId - ID do paciente
 * @param {number} documentoId - ID do documento
 * @returns {Blob} - Arquivo PDF
 */
export const downloadDocumentoPaciente = async (pacienteId, documentoId) => {
    return api.get(`/pacientes/${pacienteId}/documentos/${documentoId}/download`, {
        responseType: 'blob'
    });
};

/**
 * Obtém URL para visualizar PDF no navegador (inline)
 * @param {number} pacienteId - ID do paciente
 * @param {number} documentoId - ID do documento
 * @returns {string} - URL para visualização
 */
export const visualizarDocumentoPaciente = (pacienteId, documentoId) => {
    const baseURL = getBaseURL();
    const token = localStorage.getItem('token');
    // Usar header Authorization em vez de query param para segurança
    return `${baseURL}/pacientes/${pacienteId}/documentos/${documentoId}/view`;
};

/**
 * Atualiza metadados de um documento
 * @param {number} pacienteId - ID do paciente
 * @param {number} documentoId - ID do documento
 * @param {object} metadados - Metadados { titulo, descricao, categoria }
 */
export const atualizarDocumentoPaciente = async (pacienteId, documentoId, metadados) => {
    return api.put(`/pacientes/${pacienteId}/documentos/${documentoId}`, metadados);
};

/**
 * Deleta um documento (soft delete)
 * @param {number} pacienteId - ID do paciente
 * @param {number} documentoId - ID do documento
 */
export const deletarDocumentoPaciente = async (pacienteId, documentoId) => {
    return api.delete(`/pacientes/${pacienteId}/documentos/${documentoId}`);
};

// ============================================================================
// FUNÇÕES DE API: Recuperação de Senha
// ============================================================================

/**
 * Solicita recuperação de senha
 * @param {string} email - Email do usuário
 */
export const forgotPassword = async (email) => {
    return api.post('/auth/forgot-password', { email });
};

/**
 * Verifica se um token de recuperação é válido
 * @param {string} token - Token de recuperação
 */
export const verifyResetToken = async (token) => {
    return api.get(`/auth/verify-reset-token/${token}`);
};

/**
 * Redefine a senha usando um token
 * @param {string} token - Token de recuperação
 * @param {string} password - Nova senha
 * @param {string} confirmPassword - Confirmação da nova senha
 */
export const resetPassword = async (token, password, confirmPassword) => {
    return api.post('/auth/reset-password', {
        token,
        password,
        confirmPassword
    });
};

// --------------------------------------------------------------------------------
// 14. Exportações Nomeadas para métodos HTTP genéricos (Mantido)
// --------------------------------------------------------------------------------

export const { 
    get, post, put, patch, delete: remove 
} = api;

export default api;